import { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Input, Dropdown } from "antd";
import {
	Building2,
	MapPin,
	Zap,
	AlertTriangle,
	XCircle,
	Clock,
	ChevronRight,
	ChevronDown,
	Plus,
	Search,
	ClipboardCheck,
	FileText,
	Loader2,
	HelpCircle,
	HardHat
} from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import DashboardEmptyState from "components/dashboard-empty-state/dashboard-empty-state";
import PlanReviewsList from "components/plan-reviews-list/plan-reviews-list";
import SetupMeetingPrompt from "components/setup-meeting-prompt/setup-meeting-prompt";
import routeNames from "constants/route-names";
import { getBuildingTypeLabel, getInspectionTypeLabel, getPropertyAddress } from "utils/helpers";
import {
	InspectionGapAnalysisStatus,
	InspectionGapAnalysisSeverity,
	InspectionJobStatus
} from "constants/field-values";

import {
	useGetFilteredInspectionJobsQuery,
	GetFilteredInspectionJobsQuery
} from "graphql/queries/get-filtered-inspection-jobs.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";

type InspectionJob = GetFilteredInspectionJobsQuery["inspection_jobs"][number];

/** Shape of a single gap-analysis result row, derived from the query type.
 *  Exported so inspection-details (which uses the same DB shape via its own
 *  subscription) can reference this type alias without duplicating it. */
export type GapAnalysisItem = InspectionJob["inspection_checklist_items"][number]["gap_analysis_items"][number];

/** Overall Ollie readiness label shown in the top stat card and dashboard:
 *  in-progress → job is not yet COMPLETE (Ollie still working)
 *  fast-lane   → COMPLETE, both inspection-ready AND likely to pass: no blocking gaps,
 *                no FAIL/WARNING gaps, and Ollie score ≥ FAST_LANE_MIN_SCORE. On-site
 *                verifications alone do NOT disqualify Fast Lane — a property can be in
 *                very good condition while still needing a few items verified on-site.
 *  review      → COMPLETE, but needs a closer look: has any FAIL/WARNING gap, or is
 *                otherwise clean but scored below FAST_LANE_MIN_SCORE.
 *  not-ready   → COMPLETE, has CRITICAL/HIGH FAIL gaps */
export type ReadinessStatus = "fast-lane" | "review" | "not-ready" | "in-progress";

/** Minimum Ollie score (0–100) for a job to qualify as Fast Lane. A clean job (no
 *  blocking or FAIL/WARNING gaps) that scores below this still routes to "review":
 *  Fast Lane means both inspection-ready AND likely to pass. */
export const FAST_LANE_MIN_SCORE = 90;

interface DashboardProperty {
	job: InspectionJob;
	id: string;
	jobId: string;
	name: string;
	address: string;
	type: string;
	inspectionType: string;
	photoUrl?: string;
	ollieStatus: ReadinessStatus;
	ollieScore: number | null;
	estimatedTime: string;
}

const statusConfig: Record<ReadinessStatus, { label: string; icon: typeof Zap; badgeClass: string }> = {
	"fast-lane": { label: "Fast Lane", icon: Zap, badgeClass: "id-status-fast-lane" },
	review: { label: "Review", icon: AlertTriangle, badgeClass: "id-status-review" },
	"not-ready": { label: "Not Ready", icon: XCircle, badgeClass: "id-status-not-ready" },
	"in-progress": { label: "In progress", icon: HelpCircle, badgeClass: "id-status-review" }
};

const filterTabs = [
	{ id: "all", label: "All" },
	{ id: "fast-lane", label: "Fast Lane" },
	{ id: "review", label: "Review" },
	{ id: "not-ready", label: "Not Ready" },
	{ id: "in-progress", label: "In progress" }
] as const;

const statItems = [
	{ id: "all", label: "Total", cardClass: "id-stat-card-total" },
	{ id: "fast-lane", label: "Fast Lane", cardClass: "id-stat-card-fast-lane" },
	{ id: "review", label: "Review", cardClass: "id-stat-card-review" },
	{ id: "not-ready", label: "Not Ready", cardClass: "id-stat-card-not-ready" }
] as const;

/** Returns true when a gap is a hard blocker — status FAIL with CRITICAL or HIGH severity.
 *  Blocking gaps cause the overall readiness to become "not-ready". */
export const isBlockingGap = (gap: GapAnalysisItem): boolean =>
	gap.status === InspectionGapAnalysisStatus.FAIL &&
	(gap.severity === InspectionGapAnalysisSeverity.CRITICAL || gap.severity === InspectionGapAnalysisSeverity.HIGH);

/** Derives the top-level ReadinessStatus for a whole inspection job.
 *  Accepts the flattened job state, pre-collected gap list, and the Ollie score so
 *  that both the dashboard (query) and inspection-details (subscription) can call it
 *  with the same arguments despite sourcing data from different GQL operations.
 *
 *  Rules (evaluated in priority order):
 *    1. Job not yet COMPLETE → "in-progress" (Ollie still working)
 *    2. Any blocking gap (CRITICAL/HIGH FAIL) → "not-ready"
 *    3. Any FAIL/WARNING gap → "review"
 *    4. Ollie score ≥ FAST_LANE_MIN_SCORE → "fast-lane" (ready and likely to pass;
 *       on-site verifications alone do NOT block Fast Lane)
 *    5. Otherwise → "review" (clean, but scored too low to fast-track) */
export const deriveReadiness = (
	jobState: InspectionJobStatus | undefined,
	gaps: GapAnalysisItem[],
	ollieScore: number | null | undefined
): ReadinessStatus => {
	if (jobState !== InspectionJobStatus.COMPLETE) return "in-progress";
	if (gaps.some(isBlockingGap)) return "not-ready";
	if (
		gaps.some(g => g.status === InspectionGapAnalysisStatus.FAIL || g.status === InspectionGapAnalysisStatus.WARNING)
	) {
		return "review";
	}
	return (ollieScore ?? 0) >= FAST_LANE_MIN_SCORE ? "fast-lane" : "review";
};

/** Formats a raw minute count into a human-readable duration string.
 *  Examples: 45 → "45 min"  |  90 → "1h 30m"  |  120 → "2h"  |  0 → "—" */
export const formatEstimatedTime = (minutes: number | null | undefined): string => {
	if (!minutes || minutes <= 0) return "—";
	if (minutes < 60) return `${minutes} min`;
	const hours = Math.floor(minutes / 60);
	const remainder = minutes % 60;
	return remainder === 0 ? `${hours}h` : `${hours}h ${remainder}m`;
};

/** Minimal checklist-item shape needed to detect a never-inspected ("skipped") item.
 *  Both the dashboard query and the details subscription satisfy this shape. */
type SkippableChecklistItem = {
	gap_analysis_items: unknown[];
	evidence_findings: unknown[];
	evidence_submissions: unknown[];
};

/** True when a checklist item has no gaps, findings, or submissions —
 * it was never inspected and counts as "skipped" for the on-site time estimate. */
export const isSkippedChecklistItem = (item: SkippableChecklistItem): boolean =>
	item.gap_analysis_items.length === 0 && item.evidence_findings.length === 0 && item.evidence_submissions.length === 0;

// Total estimated on-site minutes for a job: the base estimate plus 5 minutes per skipped item.
export const computeEstimatedOnsiteMinutes = (
	baseMinutes: number | null | undefined,
	checklistItems: SkippableChecklistItem[]
): number => (baseMinutes ?? 0) + checklistItems.filter(isSkippedChecklistItem).length * 5;

type DashboardTab = "plan-reviews" | "inspections";

const InspectorDashboard = () => {
	// Tells Ollie what the reader is looking at.
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen:
				"The Ollie operations dashboard: the list of inspections and plan reviews the reader has access to, with their state and readiness."
		})
	);

	const { loggedUserValue } = useLoggedUser();
	const organizationImageUrl = loggedUserValue?.organization?.organizationImageURL;
	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState<string>("all");
	// The tab lives in the URL so the inspection screens can come back to the one they
	// belong to, and so a reload keeps the user where they were.
	const [searchParams, setSearchParams] = useSearchParams();
	// Plan reviews are what the reader opens the dashboard for, so they are what the bare URL
	// shows; the inspections tab is the one that names itself.
	const activeTab: DashboardTab = searchParams.get("tab") === "inspections" ? "inspections" : "plan-reviews";
	const setActiveTab = useCallback(
		(tab: DashboardTab) => setSearchParams(tab === "inspections" ? { tab } : {}, { replace: true }),
		[setSearchParams]
	);

	// "New Review" dropdown — lets the user start either flow (Figma 11873-11348).
	const newReviewItems = [
		{
			key: "plan-submittal",
			label: (
				<Link to={routeNames.PLAN_REVIEW_ADD} className="id-new-menu-item">
					<span className="id-new-menu-icon id-new-menu-icon-plan">
						<FileText size={16} />
					</span>
					<span className="id-new-menu-text">
						<span className="id-new-menu-title">Plan Submittal</span>
						<span className="id-new-menu-sub">Electronic plans &amp; calcs</span>
					</span>
				</Link>
			)
		},
		{
			key: "site-inspection",
			label: (
				<Link to={routeNames.OLLIE_INSPECTION_ADD} className="id-new-menu-item">
					<span className="id-new-menu-icon id-new-menu-icon-inspection">
						<ClipboardCheck size={16} />
					</span>
					<span className="id-new-menu-text">
						<span className="id-new-menu-title">Site Inspection</span>
						<span className="id-new-menu-sub">On-site field verification</span>
					</span>
				</Link>
			)
		}
	];

	// Hasura row-level permissions filter inspection_jobs by the session vars set in
	// auth.mjs (X-Hasura-User-Id / Property-Ids / Organization-Id), so an empty `where`
	// is enough — server returns only jobs the logged-in user is allowed to see.
	const { data, loading, error } = useGetFilteredInspectionJobsQuery({
		variables: { where: {} },
		fetchPolicy: "cache-and-network"
	});

	const properties: DashboardProperty[] = useMemo(() => {
		if (!data?.inspection_jobs) return [];
		return data.inspection_jobs.map(job => ({
			job,
			id: job.id,
			jobId: job.id,
			name: job.property?.name ?? "Untitled property",
			address: getPropertyAddress(job.property?.address, job.property?.standardized_address) || "—",
			type: getBuildingTypeLabel(job.property?.building_type),
			inspectionType: getInspectionTypeLabel(job.inspection_type),
			photoUrl: job.property?.photo_link ?? undefined,
			ollieStatus: deriveReadiness(
				job.state as InspectionJobStatus,
				job.inspection_checklist_items.flatMap(i => i.gap_analysis_items),
				job.iris_score
			),
			ollieScore: job.iris_score ?? null,
			estimatedTime: formatEstimatedTime(
				computeEstimatedOnsiteMinutes(job.estimated_onsite_minutes, job.inspection_checklist_items)
			)
		}));
	}, [data]);

	const filteredProperties = useMemo(() => {
		const term = searchTerm.toLowerCase();
		return properties.filter(property => {
			const matchesSearch = property.name.toLowerCase().includes(term) || property.address.toLowerCase().includes(term);
			const matchesStatus = statusFilter === "all" || property.ollieStatus === statusFilter;
			return matchesSearch && matchesStatus;
		});
	}, [properties, searchTerm, statusFilter]);

	const statValues = [
		properties.length,
		properties.filter(p => p.ollieStatus === "fast-lane").length,
		properties.filter(p => p.ollieStatus === "review").length,
		properties.filter(p => p.ollieStatus === "not-ready").length
	];

	const statIcons = [Building2, Zap, AlertTriangle, XCircle];

	const hasNoInspections = !error && properties.length === 0;

	return (
		<OllieLayout>
			<SetupMeetingPrompt />
			<div className="id-page">
				<div className="id-inner">
					<div className="id-header id-animate" style={{ animationDelay: "0ms" }}>
						<div className="id-header-identity">
							{organizationImageUrl && (
								<div className="id-org-logo">
									<img src={organizationImageUrl} alt={loggedUserValue?.organization?.name ?? "Organization"} />
								</div>
							)}
							<div>
								{/* TODO: Add org address  */}
								<h1 className="id-title">{loggedUserValue?.organization?.name ?? "Organization"}</h1>
								<p className="id-eyebrow">Pre-Screening Dashboard</p>
							</div>
						</div>
						<div className="id-header-actions">
							<Dropdown
								menu={{ items: newReviewItems }}
								trigger={["click"]}
								placement="bottomRight"
								overlayClassName="id-new-menu-overlay"
								dropdownRender={menu => (
									<div className="id-new-menu">
										<div className="id-new-menu-head">Start a new review</div>
										{menu}
									</div>
								)}
							>
								<button type="button" className="id-new-inspection-btn">
									<Plus size={16} className="id-new-review-plus" />
									<span>New Review</span>
									<ChevronDown size={16} className="id-new-review-caret" />
								</button>
							</Dropdown>
						</div>
					</div>

					{/* View toggle: Plan Reviews | Site Inspections (Figma 11873-10269) */}
					<div className="id-view-tabs id-animate" style={{ animationDelay: "50ms" }}>
						<button
							type="button"
							className={`id-view-tab${activeTab === "plan-reviews" ? " id-view-tab-active" : ""}`}
							onClick={() => setActiveTab("plan-reviews")}
						>
							Plan Reviews
						</button>
						<button
							type="button"
							className={`id-view-tab${activeTab === "inspections" ? " id-view-tab-active" : ""}`}
							onClick={() => setActiveTab("inspections")}
						>
							Site Inspections
						</button>
					</div>

					{activeTab === "plan-reviews" ? (
						<PlanReviewsList />
					) : (
						<>
							<div className="id-stats-grid id-animate" style={{ animationDelay: "100ms" }}>
								{statItems.map((stat, i) => {
									const Icon = statIcons[i];
									return (
										<div
											key={stat.label}
											className={`id-stat-card ${stat.cardClass}`}
											onClick={() => setStatusFilter(stat.id)}
										>
											<div className="id-stat-card-header">
												<span className="id-stat-label">{stat.label}</span>
												<Icon className="id-stat-icon" />
											</div>
											<p className="id-stat-value">{statValues[i]}</p>
										</div>
									);
								})}
							</div>

							<div className="id-filter-bar id-animate" style={{ animationDelay: "200ms" }}>
								<div className="id-search-wrap">
									<Input
										placeholder="Search properties..."
										value={searchTerm}
										onChange={e => setSearchTerm(e.target.value)}
										prefix={<Search className="id-search-icon" />}
										variant="borderless"
										size="large"
										className="id-search-input"
									/>
								</div>
								<div className="id-filter-tabs">
									{filterTabs.map(tab => (
										<button
											key={tab.id}
											onClick={() => setStatusFilter(tab.id)}
											className={`id-filter-tab${statusFilter === tab.id ? "-active" : ""}`}
										>
											{tab.label}
										</button>
									))}
								</div>
							</div>

							<div className="id-property-list">
								{loading && properties.length === 0 && (
									<div className="id-empty-state id-animate">
										<Loader2 className="rr-loading-spinner" />
										<p>Loading inspections…</p>
									</div>
								)}

								{error && (
									<div className="id-empty-state id-animate">
										<p>We couldn't load your inspections. {error.message}</p>
									</div>
								)}

								{!loading &&
									filteredProperties.length === 0 &&
									(hasNoInspections ? (
										<DashboardEmptyState
											tone="inspection"
											icon={HardHat}
											title="No Site Inspections Yet"
											description="Get an instant readiness report before scheduling an inspection."
											actionLabel="New Site Inspection"
											actionRoute={routeNames.OLLIE_INSPECTION_ADD}
										/>
									) : (
										<div className="id-empty-state id-animate" style={{ animationDelay: "300ms" }}>
											<p>No inspections match your criteria.</p>
										</div>
									))}

								{filteredProperties.map((property, index) => {
									const config = statusConfig[property.ollieStatus];
									const StatusIcon = config.icon;
									const isFastLane = property.ollieStatus === "fast-lane";

									return (
										<div
											key={property.id}
											className="id-property-item id-animate"
											style={{ animationDelay: `${300 + index * 80}ms` }}
										>
											<Link
												to={routeNames.OLLIE_INSPECTION_DETAILS.replace(":jobId", property.jobId)}
												className={`id-property-card${isFastLane ? "-fast-lane" : ""}`}
											>
												{isFastLane && <div className="id-fast-lane-glow" />}

												<div className="id-property-card-inner">
													<div className="id-left-section">
														{property.photoUrl ? (
															<img
																src={property.photoUrl}
																alt={property.name}
																className="id-property-img"
																onError={e => {
																	(e.target as HTMLImageElement).style.display = "none";
																}}
															/>
														) : (
															<div className="id-property-icon">
																<Building2 className="id-building-icon" />
															</div>
														)}
														<div className="id-property-info">
															<h3 className="id-property-name">{property.name}</h3>
															<div className="id-property-meta">
																<span className="id-meta-item">
																	<MapPin className="id-meta-icon" />
																	<span className="id-meta-text">{property.address}</span>
																</span>
																<span className="id-meta-row">
																	<span className="id-meta-item">
																		<Building2 className="id-meta-icon" />
																		<span className="id-meta-text">{property.type}</span>
																	</span>
																	<span className="id-meta-text">{" • "}</span>
																	<span className="id-inspection-type">
																		<ClipboardCheck className="id-meta-icon" />
																		<span className="id-inspection-type-text">{property.inspectionType}</span>
																	</span>
																</span>
															</div>
														</div>
													</div>

													<div className="id-property-metrics">
														<div className="id-metric">
															<span className="id-metric-label">Status</span>
															<div className={`id-status-badge ${config.badgeClass}`}>
																<StatusIcon className="id-status-badge-icon" />
																{config.label}
															</div>
														</div>
														<div className="id-metric">
															<span className="id-metric-label">Ollie Score</span>
															<span className={`id-ollie-score${isFastLane ? "-fast-lane" : ""}`}>
																{property.ollieScore || "—"}
															</span>
														</div>
														<div className="id-metric">
															<span className="id-metric-label">Est. Time On-Site</span>
															<span className="id-estimated-time">
																<Clock className="id-clock-icon" />
																{property.estimatedTime}
															</span>
														</div>
													</div>

													<div className="id-chevron-wrap">
														<ChevronRight className="id-chevron-icon" />
													</div>
												</div>
											</Link>
										</div>
									);
								})}
							</div>
						</>
					)}
				</div>
			</div>
		</OllieLayout>
	);
};

export default InspectorDashboard;
